import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import './auth-public-theme.css';

interface AuthCardProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Optional footer link row, e.g. "Already have an account? Sign in". */
  footer?: ReactNode;
  /** Apply the current public-site auth theme. */
  publicTheme?: boolean;
}

/**
 * Shared visual shell for every auth screen. Keeps signup / signin / forgot / reset / verify
 * looking like one cohesive flow.
 */
export function AuthCard({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
  publicTheme = false,
}: AuthCardProps) {
  return (
    <div
      className={`min-h-screen pt-32 pb-20 bg-brand-secondary/30 relative overflow-hidden${
        publicTheme ? ' qh-auth-public' : ''
      }`}
    >
      <div className="blob w-[420px] h-[420px] bg-brand-accent top-[-10%] left-[-10%]" aria-hidden />
      <div className="blob w-[360px] h-[360px] bg-brand-deep bottom-[-5%] right-[-5%]" aria-hidden />

      <div className="relative z-10 max-w-lg mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/"
            className="flex items-center"
            aria-label="QuantumHire — home"
          >
            <img
              src="/logos/quantumhire-logo-poppins.png"
              alt="QuantumHire"
              className="block h-auto w-[150px] origin-left scale-[1.125]"
            />
          </Link>
          <Link to="/" className="text-xs font-black uppercase tracking-[0.2em] text-brand-muted hover:text-brand-primary transition-colors">
            ← Back to home
          </Link>
        </div>
        <div className="qh-auth-public__card bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10">
          {eyebrow && (
            <div className="flex items-center gap-2 text-brand-accent font-black text-[10px] uppercase tracking-widest mb-3">
              <ShieldCheck className="w-3 h-3" />
              {eyebrow}
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-display font-bold text-brand-primary tracking-tight mb-2">
            {title}
          </h1>
          {subtitle && <p className="text-brand-muted font-medium mb-8">{subtitle}</p>}

          <div className={subtitle ? '' : 'mt-6'}>{children}</div>
        </div>
        {footer && <div className="text-center text-sm font-medium text-brand-muted mt-6">{footer}</div>}
      </div>
    </div>
  );
}
